<script setup lang="ts">
import IconCheck from "@/assets/icons/IconCheck.vue";
import IconRefresh from "@/assets/icons/IconRefresh.vue";
import IconTrash from "@/assets/icons/IconTrash.vue";
import IconCross from "@/assets/icons/IconCross.vue";
import ProgressBar from "@/components/modules/FileUploadModal/ProgressBar.vue";
import {FileStatus} from "@/types/files";

interface Props {
  fileName: string;
  fileSize: number;
  status?: FileStatus;
  error?: null | string;
  progress?: number;
  isFileUploadingComplete?: boolean;
}

withDefaults(defineProps<Props>(), {
  status: "pending",
  error: null,
  progress: 0,
  isFileUploadingComplete: false
});

const emit = defineEmits<{
  retry: [];
  delete: [];
  cancel: [];
}>();

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " Б";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " КБ";
  return (bytes / (1024 * 1024)).toFixed(2) + " МБ";
};
</script>

<template>
  <div class="upload-file-item">
    <div class="upload-file-item__info">
      <div class="upload-file-item__header">
        <span
          :class="{'upload-file-item__name--error': error || status === 'validation_error'}"
          class="upload-file-item__name"
        >{{ fileName }}</span>
        <div class="upload-file-item__actions">
          <button
            v-if="status === 'validated' && isFileUploadingComplete"
            class="upload-file-item__action upload-file-item__action--success"
            title="Успешно загружено"
          >
            <IconCheck />
          </button>
          <template v-else>
            <button
              class="upload-file-item__action"
              title="Повторить загрузку"
              @click="emit('retry')"
            >
              <IconRefresh />
            </button>
            <button
              class="upload-file-item__action"
              title="Удалить файл"
              @click="emit('delete')"
            >
              <IconTrash />
            </button>
            <button
              v-if="status === 'uploading'"
              class="upload-file-item__action"
              title="Отменить загрузку"
              @click="emit('cancel')"
            >
              <IconCross />
            </button>
          </template>
        </div>
      </div>
      <span
        v-if="!error && status === 'validated'"
        class="upload-file-item__size"
      >{{ formatFileSize(fileSize) }}</span>
      <span
        v-else-if="error || status === 'validation_error'"
        class="upload-file-item__error"
      >Ошибка загрузки</span>
    </div>
    <ProgressBar
      v-if="status === 'uploading' || isFileUploadingComplete"
      :progress="progress"
      :is-uploading="status === 'uploading'"
    />
  </div>
</template>

<style scoped lang="scss">
.upload-file-item {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__name {
    color: var(--black-black90);
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;

    &--error {
      color: var(--system-red);
    }
  }

  &__size {
    color: var(--gray-gray90);
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
  }

  &__error {
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    color: var(--system-red);
  }

  &__actions {
    display: flex;
    gap: 2px;
  }

  &__action {
    appearance: none;
    background-color: transparent;
    border: none;
    padding: 4px;
    color: var(--icon-icon-secondary);
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    user-select: none;

    &:hover {
      color: var(--icon-icon-on);
    }

    &--success {
      color: #36AE2B;
      cursor: default;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
}
</style>